import React from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useGetLoanApplication } from '../../services/loanService';
import { Collateral } from '../../services/schemas/loanServiceSchemas/collateral';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { DocumentPreviewModal, useDocumentPreview } from './DocumentPreview';

/**
 * What the collateral officer concluded, for credit to weigh before it decides.
 *
 * Collateral is verified before credit assessment, and a rejection comes through to credit
 * flagged rather than ending the application, so this card leads with the outcome. The detail --
 * each asset, its checks, report, checklist and photos -- sits in a side pane that opens on
 * request, so it does not crowd the assessment it supports.
 *
 * Read-only by design, like VerificationSummary.
 */

interface CollateralSummaryProps {
  applicationId?: string;
}

const ITEM_STATUS: Record<string, { label: string; className: string; icon: string }> = {
  APPROVE: { label: 'Approved', className: 'bg-success-subtle text-success-emphasis', icon: 'fa-check' },
  REJECT: { label: 'Rejected', className: 'bg-danger-subtle text-danger-emphasis', icon: 'fa-times' },
  PENDING: { label: 'Not verified', className: 'bg-secondary-subtle text-secondary-emphasis', icon: 'fa-clock-o' }
};

const ItemStatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const style = ITEM_STATUS[status || 'PENDING'] || ITEM_STATUS.PENDING;
  return (
    <span className={`badge rounded-pill ${style.className}`}>
      <i className={`fa ${style.icon} me-1`}></i>
      {style.label}
    </span>
  );
};

const humanise = (value?: string) =>
  value ? value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ') : '';

const CollateralSummary: React.FC<CollateralSummaryProps> = ({ applicationId }) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const documentPreview = useDocumentPreview(message => toast.error(message));

  const { data: application, isLoading } = useGetLoanApplication(
    applicationId || '',
    { query: { enabled: !!applicationId } }
  );

  if (!applicationId || isLoading || !application) return null;

  const collaterals: Collateral[] = application.collaterals || [];
  const unsecured = application.loanDetails?.securityType === 'UNSECURED';

  // No collateral to weigh: say so plainly rather than leaving credit to wonder.
  if (unsecured || collaterals.length === 0) {
    return (
      <div className="card mb-3">
        <div className="card-body py-2 d-flex align-items-center gap-2">
          <i className="fa fa-shield text-muted"></i>
          <span className="fw-semibold small">Collateral</span>
          <span className="text-muted small">
            {unsecured ? 'None. This is an unsecured loan.' : 'None was offered with this application.'}
          </span>
        </div>
      </div>
    );
  }

  const outcome = application.collateralOutcome;
  const inProgress = !outcome && application.status === 'COLLATERAL_VERIFICATION';
  const approved = collaterals.filter(c => c.verificationStatus === 'APPROVE');
  const rejected = collaterals.filter(c => c.verificationStatus === 'REJECT');
  const totalValue = collaterals.reduce((sum, c) => sum + (c.value || 0), 0);

  const outcomeBadge = outcome === 'APPROVE'
    ? { label: 'Collateral approved', className: 'bg-success-subtle text-success-emphasis', icon: 'fa-check-circle' }
    : outcome === 'REJECT'
      ? { label: 'Collateral rejected', className: 'bg-danger-subtle text-danger-emphasis', icon: 'fa-times-circle' }
      : inProgress
        ? { label: 'Verification in progress', className: 'bg-warning-subtle text-warning-emphasis', icon: 'fa-clock-o' }
        : { label: 'Not yet verified', className: 'bg-secondary-subtle text-secondary-emphasis', icon: 'fa-clock-o' };

  const documentButton = (key: string | undefined, label: string, name: string) => key ? (
    <Button
      key={label}
      variant="outline-secondary"
      size="sm"
      onClick={() => documentPreview.open(key, name)}
      disabled={documentPreview.openingKey === key}
    >
      <i className={`fa ${documentPreview.openingKey === key ? 'fa-spinner fa-spin' : 'fa-eye'} me-1`}></i>
      {label}
    </Button>
  ) : null;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2 py-2">
        <h6 className="card-title mb-0">
          <i className="fa fa-shield me-2"></i>
          Collateral
        </h6>
        <div className="d-flex align-items-center gap-2">
          <span className={`badge rounded-pill ${outcomeBadge.className}`}>
            <i className={`fa ${outcomeBadge.icon} me-1`}></i>
            {outcomeBadge.label}
          </span>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowDetails(true)}>
            <i className="fa fa-columns me-1"></i>
            Show collateral
          </Button>
        </div>
      </div>

      <div className="card-body">
        <div className="small text-muted">
          {collaterals.length} {collaterals.length === 1 ? 'asset' : 'assets'} offered
          {totalValue > 0 && <> &middot; total value {formatCurrency(totalValue)}</>}
          {(approved.length > 0 || rejected.length > 0) && (
            <> &middot; {approved.length} approved, {rejected.length} rejected</>
          )}
        </div>

        {/* A rejection is what credit most needs to see, so it reads without opening the pane. */}
        {outcome === 'REJECT' && (
          <div className="alert alert-danger mt-3 mb-0">
            <div className="fw-semibold mb-1">
              <i className="fa fa-exclamation-triangle me-2"></i>
              The collateral officer rejected the collateral
            </div>
            {application.collateralOutcomeNotes && (
              <div className="small mb-1">{application.collateralOutcomeNotes}</div>
            )}
            {rejected.length > 0 && (
              <ul className="small mb-0">
                {rejected.map((c, index) => (
                  <li key={`${c.assetDescription}-${index}`}>
                    {c.assetDescription || humanise(c.collateralType) || 'Asset'}
                    {c.verificationNotes && <span className="text-muted"> &mdash; {c.verificationNotes}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {outcome === 'APPROVE' && application.collateralOutcomeNotes && (
          <div className="small mt-2">{application.collateralOutcomeNotes}</div>
        )}

        {application.collateralVerifiedAt && (
          <div className="small text-muted mt-2">
            <i className="fa fa-user-o me-1"></i>
            {application.collateralVerifiedByName ? `Verified by ${application.collateralVerifiedByName}` : 'Verified'}
            {` · ${formatDateTime(application.collateralVerifiedAt)}`}
          </div>
        )}
      </div>

      <Offcanvas show={showDetails} onHide={() => setShowDetails(false)} placement="end" style={{ width: 'min(520px, 100vw)' }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="fs-6">
            <i className="fa fa-shield me-2"></i>
            Collateral &middot; {application.applicationNumber}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3">
            <span className={`badge rounded-pill ${outcomeBadge.className}`}>
              <i className={`fa ${outcomeBadge.icon} me-1`}></i>
              {outcomeBadge.label}
            </span>
            {application.collateralOutcomeNotes && (
              <p className="small mt-2 mb-0">{application.collateralOutcomeNotes}</p>
            )}
          </div>

          {collaterals.map((c, index) => {
            const photos = [c.photoDocumentKey1, c.photoDocumentKey2, c.photoDocumentKey3, c.photoDocumentKey4, c.photoDocumentKey5, c.photoDocumentKey6]
              .filter((key): key is string => !!key);
            const details = [
              { label: 'Type', value: humanise(c.collateralType) },
              { label: 'Value', value: c.value != null ? formatCurrency(c.value) : '' },
              { label: 'Make', value: c.make },
              { label: 'Year / model', value: c.yearModel },
              { label: 'Registration', value: c.registrationNumber },
              { label: 'Owner', value: [c.ownerName, c.ownerId].filter(Boolean).join(' · ') },
              { label: 'Other details', value: c.otherDetails }
            ].filter(detail => detail.value);

            return (
              <div key={`${c.assetDescription}-${index}`} className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                  <div className="fw-semibold">{c.assetDescription || `Asset ${index + 1}`}</div>
                  <ItemStatusBadge status={c.verificationStatus} />
                </div>

                <dl className="row small mb-2">
                  {details.map(detail => (
                    <React.Fragment key={detail.label}>
                      <dt className="col-5 text-muted fw-normal">{detail.label}</dt>
                      <dd className="col-7 mb-1">{detail.value}</dd>
                    </React.Fragment>
                  ))}
                </dl>

                {c.verificationNotes && (
                  <div className={`small mb-2 ${c.verificationStatus === 'REJECT' ? 'text-danger' : ''}`}>
                    <i className="fa fa-comment-o me-1"></i>
                    {c.verificationNotes}
                  </div>
                )}

                <div className="d-flex flex-wrap gap-2">
                  {documentButton(c.collateralReportDocumentKey, 'Report', `Collateral report - ${c.assetDescription || index + 1}`)}
                  {documentButton(c.collateralChecklistDocumentKey, 'Checklist', `Verification checklist - ${c.assetDescription || index + 1}`)}
                  {photos.map((key, photoIndex) =>
                    documentButton(key, `Photo ${photoIndex + 1}`, `Photo ${photoIndex + 1} - ${c.assetDescription || index + 1}`)
                  )}
                  {!c.collateralReportDocumentKey && !c.collateralChecklistDocumentKey && photos.length === 0 && (
                    <span className="small text-muted">No report, checklist or photos attached.</span>
                  )}
                </div>
              </div>
            );
          })}
        </Offcanvas.Body>
      </Offcanvas>

      <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
    </div>
  );
};

export default CollateralSummary;
